import { LOCALE_CODES, TrialInterval } from '@global/types/types';
import { SiteConfig } from './SiteConfig';

const defaultFormatterOptions: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
};

export const dateFormatter = (
  date,
  locale: LOCALE_CODES,
  options = defaultFormatterOptions
) => {
  return new Date(date).toLocaleString(locale, options);
};

type DateMask = 'DM' | 'DMY' | 'MY' | 'Y';

const maskOptions: Record<DateMask, Intl.DateTimeFormatOptions> = {
  DM: { day: 'numeric', month: 'long' },
  DMY: { day: 'numeric', month: 'long', year: 'numeric' },
  MY: { month: 'long', year: 'numeric' },
  Y: { year: 'numeric' },
};

export const dateMaskFormatter = (
  date: string | Date,
  mask: DateMask
): string => {
  const { locale } = SiteConfig.getConfig();
  const options = maskOptions[mask] || maskOptions.DMY;
  return new Date(date).toLocaleString(locale, options);
};

export const dateFormatterShort = (date, locale: LOCALE_CODES) => {
  return new Date(date).toLocaleString(locale, {
    month: 'long',
    year: 'numeric',
  });
};

const getUTCDateComponents = (inputDate: string | Date): number[] => {
  const date = typeof inputDate === 'string' ? new Date(inputDate) : inputDate;
  return [
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
    date.getUTCMilliseconds(),
  ];
};

export const addMonthsUTC = (date: string | Date, months: number): Date => {
  const [year, month, day, hours, minutes, seconds, milliseconds] =
    getUTCDateComponents(date);
  const result = new Date(
    Date.UTC(year, month + months, day, hours, minutes, seconds, milliseconds)
  );

  if (result.getUTCDate() !== day) {
    result.setUTCDate(0);
  }

  return result;
};

export const addDaysUTC = (date: string | Date, days: number): Date => {
  const [year, month, day, hours, minutes, seconds, milliseconds] =
    getUTCDateComponents(date);
  const result = new Date(
    Date.UTC(year, month, day + days, hours, minutes, seconds, milliseconds)
  );

  return result;
};

export const addFreeTrialDates = (
  date: Date,
  amount: number,
  units: TrialInterval
): string => {
  const dateProcessor = units === TrialInterval.DAY ? addDaysUTC : addMonthsUTC;
  const newDate = dateProcessor(date, amount);
  const result = dateMaskFormatter(newDate, 'DM');

  return result;
};

export type Suffixes = {
  one: string;
  two: string;
  few: string;
  other: string;
};

export const getSubscriptionInfoDate = (
  isFreeTrial: boolean,
  trialDays: number,
  suffixes: Suffixes
): { day: number; month: string; daySuffix: string } => {
  const timestamp = isFreeTrial
    ? addDaysUTC(new Date(), trialDays)
    : new Date();
  const day = timestamp.getDate();
  const { locale } = SiteConfig.getConfig();
  const month = dateFormatter(timestamp, locale, { month: 'long' });
  const daySuffix = getOrdinalSuffix(day, locale, suffixes);
  return { day, month, daySuffix };
};

export const getOrdinalSuffix = (
  day: number,
  locale: string,
  suffixes: Suffixes
): string => {
  const pr = new Intl.PluralRules(locale, { type: 'ordinal' });
  const suffix = suffixes[pr.select(day)];

  return suffix;
};
